import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import {
  Database, Download, Upload, Lock, Eye, EyeOff, ShieldCheck, Loader2,
  CloudOff, FileWarning, AlertTriangle, ArrowDownToLine, ArrowUpFromLine, X,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const LAST_BACKUP_KEY = "rocklink.lastBackupAt";

/* ── Tiny helpers ──────────────────────────────────────────────────────── */
function formatBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatRelative(iso) {
  if (!iso) return "Never";
  try {
    const ts = new Date(iso).getTime();
    const secs = Math.max(0, (Date.now() - ts) / 1000);
    if (secs < 60) return "Just now";
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
  } catch { return "—"; }
}

/* ── Compact passphrase field with eye toggle ──────────────────────────── */
function PassField({ value, onChange, autoFocus, placeholder, testId }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Lock className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] pointer-events-none" />
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || "Enter a strong passphrase…"}
        autoFocus={autoFocus}
        autoComplete="off"
        className="w-full h-10 pl-9 pr-9 rounded-lg bg-[hsl(var(--muted))]/60 border border-[hsl(var(--border))] text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/60 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors font-mono-data"
        data-testid={testId}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] transition-colors"
        aria-label={show ? "Hide passphrase" : "Show passphrase"}
        tabIndex={-1}
      >
        {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

/* ── Create backup modal ───────────────────────────────────────────────── */
function CreateBackupModal({ open, onClose, onCreated }) {
  const [pass1, setPass1] = useState("");
  const [pass2, setPass2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) { setPass1(""); setPass2(""); setBusy(false); } }, [open]);

  const valid = pass1.length >= 8 && pass1 === pass2;
  const showMismatch = pass2.length > 0 && pass1 !== pass2;

  const handleDownload = useCallback(async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/backup/create`,
        { passphrase: pass1 },
        { responseType: "blob" }
      );
      // Pull metadata from the response headers we set on the backend.
      const items = res.headers["x-backup-items"] || "?";
      const users = res.headers["x-backup-users"] || "?";
      const filename = res.headers["x-backup-filename"] || `rocklink-${Date.now()}.rlbackup`;
      const createdAt = res.headers["x-backup-created-at"] || new Date().toISOString();

      // Trigger the browser download.
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.style.display = "none";
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      // Persist "last backup" timestamp locally so the panel stays in sync.
      try { localStorage.setItem(LAST_BACKUP_KEY, JSON.stringify({ createdAt, items, users, filename, size: res.data.size })); } catch (_e) { /* ignore */ }
      toast.success(`Backup downloaded · ${items} items · ${users} users`);
      onCreated?.({ createdAt, items, users, filename, size: res.data.size });
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Backup failed";
      toast.error(msg);
    } finally { setBusy(false); }
  }, [valid, pass1, onCreated, onClose]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] w-[95vw] md:w-[480px] max-w-[95vw] p-0 overflow-hidden" data-testid="backup-create-modal">
        <div className="h-[3px] w-full bg-gradient-to-r from-orange-500 to-violet-500" />
        <DialogHeader className="px-6 pt-5 pb-3">
          <DialogTitle className="flex items-center gap-2 text-base">
            <ArrowDownToLine className="w-4 h-4 text-orange-500" />
            <span>Create backup</span>
          </DialogTitle>
          <DialogDescription className="text-xs leading-relaxed">
            Build an encrypted <span className="font-mono-data">.rlbackup</span> file with everything in RockLink — items, users, settings, and history log. Your passphrase encrypts the file end-to-end. <strong className="text-amber-500">Without it, the backup cannot be restored.</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-4 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-1.5 block">Passphrase</label>
            <PassField value={pass1} onChange={setPass1} autoFocus testId="backup-create-pass1" />
            <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">At least 8 characters. Save it in a password manager — there is no recovery.</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-1.5 block">Confirm passphrase</label>
            <PassField value={pass2} onChange={setPass2} testId="backup-create-pass2" />
            {showMismatch && <p className="text-[11px] text-red-500 mt-1">Passphrases don't match.</p>}
          </div>

          <div className="rounded-lg border border-orange-500/20 bg-orange-500/[0.04] px-3 py-2">
            <p className="text-[11px] text-[hsl(var(--foreground))] font-semibold flex items-center gap-1.5"><ShieldCheck className="w-3 h-3 text-orange-500" /> AES-256-GCM · scrypt KDF</p>
            <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">Header (date, item count) is unencrypted so you can preview the file. Everything sensitive is encrypted with a key derived from your passphrase.</p>
          </div>
        </div>

        <div className="px-6 py-3 flex items-center justify-between border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30">
          <button onClick={onClose} className="text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors" data-testid="backup-create-cancel-btn">
            Cancel
          </button>
          <button
            onClick={handleDownload}
            disabled={!valid || busy}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: valid && !busy ? "linear-gradient(135deg, #f97316, #8b5cf6)" : "rgba(99,102,241,0.4)" }}
            data-testid="backup-create-download-btn"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {busy ? "Building backup…" : "Download to this device"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Restore modal ─────────────────────────────────────────────────────── */
function RestoreBackupModal({ open, onClose, onRestored }) {
  // Step 1 → choose file. Step 2 → preview header, enter passphrase. Step 3 → typed confirm.
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);   // { header, size_bytes }
  const [pass, setPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = useCallback(() => {
    setStep(1); setFile(null); setPreview(null); setPass(""); setConfirm(""); setBusy(false);
  }, []);
  useEffect(() => { if (!open) reset(); }, [open, reset]);

  const onPick = useCallback(async (f) => {
    if (!f) return;
    if (!f.name.endsWith(".rlbackup")) {
      toast.error("Pick a RockLink backup file (.rlbackup).");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await axios.post(`${API}/api/admin/backup/preview`, fd);
      setFile(f);
      setPreview(res.data);
      setStep(2);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Couldn't read file";
      toast.error(msg);
    } finally { setBusy(false); }
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    const f = e.dataTransfer.files?.[0];
    if (f) onPick(f);
  }, [onPick]);

  const handleRestore = useCallback(async () => {
    if (confirm !== "RESTORE" || !file || !pass) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("passphrase", pass);
      fd.append("confirm", "RESTORE");
      const res = await axios.post(`${API}/api/admin/backup/restore`, fd);
      const sum = res.data.restored || {};
      toast.success(`Restored · ${sum.line_items ?? 0} items · ${sum.users ?? 0} users`);
      onRestored?.(res.data);
      onClose();
      // Force a clean reload — the user list, settings, and items collection
      // have all been replaced under the running app. A reload guarantees
      // every cached query sees the new state.
      setTimeout(() => { window.location.reload(); }, 400);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Restore failed";
      toast.error(msg);
    } finally { setBusy(false); }
  }, [file, pass, confirm, onRestored, onClose]);

  const counts = preview?.header?.counts || {};
  const created = preview?.header?.created_at;
  const company = preview?.header?.company;
  const createdBy = preview?.header?.created_by;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] w-[95vw] md:w-[520px] max-w-[95vw] p-0 overflow-hidden" data-testid="backup-restore-modal">
        <div className="h-[3px] w-full bg-gradient-to-r from-amber-500 to-rose-500" />
        <DialogHeader className="px-6 pt-5 pb-3">
          <DialogTitle className="flex items-center gap-2 text-base">
            <ArrowUpFromLine className="w-4 h-4 text-amber-500" />
            <span>Restore backup</span>
          </DialogTitle>
          <DialogDescription className="text-xs leading-relaxed">
            Restoring will <strong className="text-amber-500">replace everything currently in RockLink</strong> with the contents of the backup file. A safety snapshot of the current state is saved automatically before the restore runs.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-4">
          {/* Tabs — only "From file" is wired today; "From cloud" is a teaser */}
          <div className="flex items-center gap-1 mb-3 p-1 rounded-lg bg-[hsl(var(--muted))]/40">
            <button className="flex-1 text-xs font-semibold py-1.5 rounded-md bg-[hsl(var(--card))] text-[hsl(var(--foreground))] shadow-sm" data-testid="backup-restore-tab-file">
              From file
            </button>
            <button
              disabled
              className="flex-1 text-xs font-semibold py-1.5 rounded-md text-[hsl(var(--muted-foreground))] cursor-not-allowed flex items-center justify-center gap-1"
              title="Cloud storage isn't connected yet"
              data-testid="backup-restore-tab-cloud"
            >
              <CloudOff className="w-3 h-3" /> From cloud
            </button>
          </div>

          {step === 1 && (
            <label
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
              onDrop={onDrop}
              className="block border-2 border-dashed border-[hsl(var(--border))] rounded-lg px-4 py-8 text-center cursor-pointer hover:border-amber-500/50 hover:bg-amber-500/[0.03] transition-colors"
              data-testid="backup-restore-dropzone"
            >
              <Upload className="w-7 h-7 mx-auto mb-2 text-[hsl(var(--muted-foreground))]" />
              <p className="text-sm font-semibold text-[hsl(var(--foreground))]">Drop your <span className="font-mono-data">.rlbackup</span> file here</p>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">or click to browse</p>
              <input
                type="file"
                accept=".rlbackup"
                onChange={(e) => onPick(e.target.files?.[0])}
                className="hidden"
                data-testid="backup-restore-file-input"
              />
            </label>
          )}

          {step === 2 && preview && (
            <>
              <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 p-3 mb-3" data-testid="backup-restore-preview">
                <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-1.5">What's inside</p>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12px]">
                  <span className="text-[hsl(var(--muted-foreground))]">Created</span>
                  <span className="font-mono-data text-[hsl(var(--foreground))]">{created ? new Date(created).toLocaleString() : "—"}</span>
                  <span className="text-[hsl(var(--muted-foreground))]">By</span>
                  <span className="font-mono-data text-[hsl(var(--foreground))] truncate">{createdBy || "—"}</span>
                  <span className="text-[hsl(var(--muted-foreground))]">Company</span>
                  <span className="font-mono-data text-[hsl(var(--foreground))] truncate">{company || "—"}</span>
                  <span className="text-[hsl(var(--muted-foreground))]">File size</span>
                  <span className="font-mono-data text-[hsl(var(--foreground))]">{formatBytes(preview.size_bytes)}</span>
                </div>
                <div className="mt-2 pt-2 border-t border-[hsl(var(--border))]/50 flex items-center justify-around text-center">
                  <Stat n={counts.line_items} label="Items" />
                  <Stat n={counts.users} label="Users" />
                  <Stat n={counts.settings} label="Settings" />
                  <Stat n={counts.changelog} label="History" />
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-1.5 block">Passphrase</label>
                <PassField value={pass} onChange={setPass} autoFocus placeholder="The passphrase used when the backup was made" testId="backup-restore-pass" />
              </div>

              <div className="flex items-center justify-between mt-3">
                <button onClick={reset} className="text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="backup-restore-back-btn">
                  ← Pick a different file
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={!pass}
                  className="px-3 py-1.5 rounded-md text-xs font-bold text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="backup-restore-continue-btn"
                >
                  Continue →
                </button>
              </div>
            </>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <div className="rounded-lg border border-rose-500/30 bg-rose-500/[0.06] p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
                <div className="text-[12px] text-[hsl(var(--foreground))] leading-relaxed">
                  <p className="font-semibold">This action will replace every item, user, setting, and history log entry currently in RockLink.</p>
                  <p className="text-[hsl(var(--muted-foreground))] mt-1">Type <span className="font-mono-data font-bold text-rose-500">RESTORE</span> below to continue. A safety snapshot of the current data will be saved server-side first.</p>
                </div>
              </div>
              <input
                type="text"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Type RESTORE to confirm"
                autoFocus
                className="w-full h-10 px-3 rounded-lg bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-sm font-mono-data text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/60 focus:outline-none focus:ring-2 focus:ring-rose-500 transition-colors uppercase tracking-wider"
                data-testid="backup-restore-confirm-input"
              />
              <div className="flex items-center justify-between">
                <button onClick={() => setStep(2)} className="text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
                  ← Back
                </button>
                <button
                  onClick={handleRestore}
                  disabled={confirm !== "RESTORE" || busy}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-bold text-white bg-rose-500 hover:bg-rose-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="backup-restore-execute-btn"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowUpFromLine className="w-3.5 h-3.5" />}
                  {busy ? "Restoring…" : "Restore now"}
                </button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <p className="font-mono-data font-bold text-[15px] text-[hsl(var(--foreground))] leading-tight">{(n ?? 0).toLocaleString()}</p>
      <p className="text-[9px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">{label}</p>
    </div>
  );
}

/* ── The Backup section row block (drops into AdminSettingsPanel) ──────── */
export default function BackupSection() {
  const [createOpen, setCreateOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [last, setLast] = useState(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LAST_BACKUP_KEY);
      if (raw) setLast(JSON.parse(raw));
    } catch (_e) { /* ignore */ }
  }, []);

  const lastLabel = useMemo(() => {
    if (!last) return "No backup taken yet";
    const items = last.items ?? "?";
    const size = formatBytes(last.size);
    return `Last backup: ${formatRelative(last.createdAt)} · ${items} items · ${size}`;
  }, [last]);

  return (
    <>
      {/* Section heading */}
      <div className="mb-1 mt-5">
        <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Backups</p>
      </div>

      {/* Status strip */}
      <div className="my-2 flex items-center gap-3 px-4 py-2.5 rounded-lg" style={{ background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.15)" }} data-testid="backup-status-strip">
        <Database className="w-4 h-4 text-orange-400 shrink-0" />
        <p className="text-xs text-[hsl(var(--foreground))] font-medium">{lastLabel}</p>
        <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
          <CloudOff className="w-3 h-3" /> Cloud not connected
        </span>
      </div>

      {/* Action rows */}
      <button
        onClick={() => setCreateOpen(true)}
        className="w-full flex items-center gap-4 py-4 border-b border-[hsl(var(--border))]/50 hover:bg-[hsl(var(--muted))]/30 transition-colors text-left -mx-2 px-2 rounded-md"
        data-testid="backup-action-create"
      >
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(99,102,241,0.10)" }}>
          <ArrowDownToLine className="w-4 h-4 text-orange-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[hsl(var(--foreground))]">Create backup</p>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">Encrypted bundle · downloads to your device · cloud upload coming soon</p>
        </div>
        <span className="text-[hsl(var(--muted-foreground))] text-sm">›</span>
      </button>

      <button
        onClick={() => setRestoreOpen(true)}
        className="w-full flex items-center gap-4 py-4 border-b border-[hsl(var(--border))]/50 hover:bg-[hsl(var(--muted))]/30 transition-colors text-left -mx-2 px-2 rounded-md"
        data-testid="backup-action-restore"
      >
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(245,158,11,0.10)" }}>
          <ArrowUpFromLine className="w-4 h-4 text-amber-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[hsl(var(--foreground))]">Restore backup</p>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">Replace everything with a previous backup · safety snapshot taken first</p>
        </div>
        <span className="text-[hsl(var(--muted-foreground))] text-sm">›</span>
      </button>

      <div className="w-full flex items-center gap-4 py-4 border-b border-[hsl(var(--border))]/50 opacity-60" data-testid="backup-action-cloud-disabled">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(100,116,139,0.10)" }}>
          <CloudOff className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[hsl(var(--foreground))]">Cloud storage</p>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">Connect Google Drive / OneDrive for daily auto-backups · coming soon</p>
        </div>
        <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]">Soon</span>
      </div>

      <CreateBackupModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(meta) => setLast(meta)}
      />
      <RestoreBackupModal
        open={restoreOpen}
        onClose={() => setRestoreOpen(false)}
        onRestored={() => { /* page reload happens inside the modal */ }}
      />
    </>
  );
}
